import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { userService } from '../../services/userService'
import { AiQuota, AiUsageSummary } from '../../types/auth'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/FormField'
import { TableWrapper, StyledTable, THead, TH, TR, TD, EmptyState } from '../../components/ui/Table'
import { recentMonths } from '../../utils/period'
import { tokens } from '../../styles/tokens'

const FEATURE_LABELS: Record<string, string> = {
  recommendations: 'Recommendations',
  identify_ingredients: 'Ingredient scanner',
  parse_recipe: 'Recipe from photo',
  meal_plan: 'Meal plan generator',
  recipe_translation: 'Recipe translation',
}

const featureLabel = (feature: string) => FEATURE_LABELS[feature] ?? feature
const usd = (n: number | null, digits = 2) => (n === null ? '—' : `$${n.toFixed(digits)}`)
const int = (n: number) => n.toLocaleString('en-US')

// ─── Styles ───────────────────────────────────────────────
const Section = styled.section`
  background: white;
  border-radius: 12px;
  border: 1px solid ${tokens.purple100};
  padding: 20px;
  margin-top: 20px;
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`

const SectionTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: ${tokens.purple900};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const PeriodControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;

  select,
  input {
    width: auto;
  }
`

const SubTitle = styled.h4`
  font-size: 12px;
  font-weight: 600;
  color: ${tokens.purple600};
  margin: 20px 0 10px;
`

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
`

const Tile = styled.div`
  background: ${tokens.purple50};
  border-radius: 10px;
  padding: 12px 14px;
`

const TileValue = styled.p`
  font-size: 20px;
  font-weight: 600;
  color: ${tokens.purple900};
`

const TileLabel = styled.p`
  font-size: 11px;
  color: ${tokens.purple400};
`

const QuotaRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr 80px;
  gap: 12px;
  align-items: center;
  font-size: 12px;
  color: ${tokens.purple700};
  margin-bottom: 8px;
`

const Track = styled.div`
  height: 8px;
  background: ${tokens.purple100};
  border-radius: 4px;
  overflow: hidden;
`

const Fill = styled.div<{ $pct: number; $full: boolean }>`
  height: 100%;
  width: ${p => Math.min(100, p.$pct)}%;
  background: ${p => (p.$full ? '#e24b4a' : tokens.purple400)};
`

const Chart = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 120px;
  padding: 4px 0;
  overflow-x: auto;
  border-bottom: 1px solid ${tokens.purple100};
`

const Bar = styled.div<{ $height: number; $atLimit: boolean }>`
  flex: 1 0 8px;
  min-width: 8px;
  height: ${p => p.$height}%;
  min-height: ${p => (p.$height > 0 ? 2 : 0)}px;
  background: ${p => (p.$atLimit ? '#e24b4a' : tokens.purple400)};
  border-radius: 2px 2px 0 0;
`

const ChartAxis = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: ${tokens.purple400};
  margin-top: 4px;
`

const Note = styled.p`
  font-size: 11px;
  color: ${tokens.purple400};
  margin-top: 12px;
`

const ErrorNote = styled.p`
  font-size: 12px;
  color: #c0436a;
`

// ─── Helpers ──────────────────────────────────────────────
/** Every day in [from, to] (YYYY-MM-DD, UTC) with its call count — days without calls are 0 */
function fillDays(summary: AiUsageSummary, from: string, to: string) {
  const byDate = new Map(summary.daily.map(d => [d.date, d]))
  const days: { date: string; calls: number; cost: number }[] = []
  const end = new Date(`${to}T00:00:00Z`)
  for (let d = new Date(`${from}T00:00:00Z`); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    const date = d.toISOString().slice(0, 10)
    days.push(byDate.get(date) ?? { date, calls: 0, cost: 0 })
  }
  return days
}

// ─── Component ────────────────────────────────────────────
interface Props {
  svc: ReturnType<typeof userService>
  userId: string
  quotas: AiQuota[]
  dailyLimit: number
}

const CUSTOM = 'custom'

/** AI usage audit for one user: this month's caps, then any chosen period in detail */
export function AiUsageSection({ svc, userId, quotas, dailyLimit }: Props) {
  const months = useMemo(() => recentMonths(new Date(), 12), [])
  const [periodKey, setPeriodKey] = useState(months[0].key)
  const [customFrom, setCustomFrom] = useState(months[0].from)
  const [customTo, setCustomTo] = useState(months[0].to)
  // The range actually queried — custom ranges apply on "Apply", months immediately
  const [range, setRange] = useState({ from: months[0].from, to: months[0].to })
  const [summary, setSummary] = useState<AiUsageSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    svc
      .aiUsage(userId, range.from, range.to)
      .then(data => {
        if (!cancelled) setSummary(data)
      })
      .catch((e: { message?: string }) => {
        if (!cancelled) setError(e.message ?? 'Could not load AI usage')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [svc, userId, range])

  const handlePeriodChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const key = e.target.value
      setPeriodKey(key)
      const month = months.find(m => m.key === key)
      if (month) setRange({ from: month.from, to: month.to })
    },
    [months]
  )

  const applyCustom = useCallback(
    () => setRange({ from: customFrom, to: customTo }),
    [customFrom, customTo]
  )

  const days = useMemo(
    () => (summary ? fillDays(summary, range.from, range.to) : []),
    [summary, range]
  )
  const maxCalls = Math.max(dailyLimit, ...days.map(d => d.calls))
  const daysAtLimit = days.filter(d => d.calls >= dailyLimit).length

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>AI usage</SectionTitle>
        <PeriodControls>
          <Select value={periodKey} onChange={handlePeriodChange} aria-label="Period">
            {months.map(m => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
            <option value={CUSTOM}>Custom range…</option>
          </Select>
          {periodKey === CUSTOM && (
            <>
              <Input
                type="date"
                value={customFrom}
                max={customTo}
                onChange={e => setCustomFrom(e.target.value)}
                aria-label="From"
              />
              <Input
                type="date"
                value={customTo}
                min={customFrom}
                onChange={e => setCustomTo(e.target.value)}
                aria-label="To"
              />
              <Button size="sm" onClick={applyCustom} disabled={!customFrom || !customTo}>
                Apply
              </Button>
            </>
          )}
        </PeriodControls>
      </SectionHeader>

      <SubTitle>Monthly caps — this month</SubTitle>
      {quotas.map(q => (
        <QuotaRow key={q.feature}>
          <span>{featureLabel(q.feature)}</span>
          <Track>
            <Fill $pct={(q.used / q.limit) * 100} $full={q.used >= q.limit} />
          </Track>
          <span>
            {q.used} / {q.limit}
          </span>
        </QuotaRow>
      ))}
      <Note style={{ marginTop: 4 }}>
        Plus a shared limit of {dailyLimit} AI calls per day across all features.
      </Note>

      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !summary && <Note>Loading…</Note>}

      {summary && (
        <div style={{ opacity: loading ? 0.5 : 1 }}>
          <SubTitle>
            {range.from} → {range.to} (UTC)
          </SubTitle>
          <Tiles>
            <Tile>
              <TileValue>{int(summary.totals.calls)}</TileValue>
              <TileLabel>AI calls</TileLabel>
            </Tile>
            <Tile>
              <TileValue>{usd(summary.totals.cost)}</TileValue>
              <TileLabel>Estimated cost</TileLabel>
            </Tile>
            <Tile>
              <TileValue>{int(summary.totals.inputTokens)}</TileValue>
              <TileLabel>Input tokens</TileLabel>
            </Tile>
            <Tile>
              <TileValue>{int(summary.totals.outputTokens)}</TileValue>
              <TileLabel>Output tokens</TileLabel>
            </Tile>
          </Tiles>

          <SubTitle>
            Calls per day
            {daysAtLimit > 0 &&
              ` — ${daysAtLimit} day${daysAtLimit === 1 ? '' : 's'} at the daily limit (red)`}
          </SubTitle>
          <Chart>
            {days.map(d => (
              <Bar
                key={d.date}
                $height={(d.calls / maxCalls) * 100}
                $atLimit={d.calls >= dailyLimit}
                title={`${d.date}: ${d.calls} call${d.calls === 1 ? '' : 's'}, ${usd(d.cost, 4)}`}
              />
            ))}
          </Chart>
          <ChartAxis>
            <span>{range.from}</span>
            <span>{range.to}</span>
          </ChartAxis>

          <SubTitle>By feature</SubTitle>
          <TableWrapper>
            <StyledTable>
              <THead>
                <tr>
                  <TH>Feature</TH>
                  <TH>Calls</TH>
                  <TH>Input tokens</TH>
                  <TH>Output tokens</TH>
                  <TH>Est. cost</TH>
                  <TH>Monthly cap</TH>
                </tr>
              </THead>
              <tbody>
                {summary.features.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <EmptyState message="No AI calls in this period." />
                    </td>
                  </tr>
                )}
                {summary.features.map(f => (
                  <TR key={f.feature}>
                    <TD>{featureLabel(f.feature)}</TD>
                    <TD>{int(f.calls)}</TD>
                    <TD>{int(f.inputTokens)}</TD>
                    <TD>{int(f.outputTokens)}</TD>
                    <TD>{usd(f.cost, 4)}</TD>
                    <TD>{f.monthlyLimit ?? '—'}</TD>
                  </TR>
                ))}
              </tbody>
            </StyledTable>
          </TableWrapper>

          {summary.calls.length > 0 && (
            <>
              <SubTitle>
                Calls{summary.callsTruncated ? ` — latest ${summary.calls.length}` : ''}
              </SubTitle>
              <TableWrapper>
                <StyledTable>
                  <THead>
                    <tr>
                      <TH>When (local time)</TH>
                      <TH>Feature</TH>
                      <TH>Model</TH>
                      <TH>In</TH>
                      <TH>Out</TH>
                      <TH>Est. cost</TH>
                    </tr>
                  </THead>
                  <tbody>
                    {summary.calls.map(c => (
                      <TR key={c.id}>
                        <TD>{new Date(c.createdAt).toLocaleString()}</TD>
                        <TD>{featureLabel(c.feature)}</TD>
                        <TD style={{ fontSize: 11 }}>{c.model}</TD>
                        <TD>{int(c.inputTokens)}</TD>
                        <TD>{int(c.outputTokens)}</TD>
                        <TD>{usd(c.cost, 4)}</TD>
                      </TR>
                    ))}
                  </tbody>
                </StyledTable>
              </TableWrapper>
            </>
          )}

          <Note>
            Costs are list-price estimates from the API&apos;s token counts. Per-call logging
            started on Oct 7, 2026 — earlier periods show no data.
          </Note>
        </div>
      )}
    </Section>
  )
}
